import { Fragment, useEffect, useRef, useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { fetchRunLog } from '../api.js'
import { LINE, LOG_FILTERS, createLogModel, layout } from './log-model.js'
import type { LineTone, LogFilter, LogModel, LogSegment } from './log-model.js'
import { VerdictHeadline } from './verdict.jsx'

/**
 * A run's log, as it arrives — folded, filterable and searchable (IM-166).
 *
 * This is the feature that replaces watching a terminal over a screenshare.
 * What it has to survive is the shape of a real apply: 62,388 lines, of which
 * ~59,000 are resource attribute bodies (one CRD schema alone is 19,091), ~500
 * progress ticks, and about a thousand lines anyone reads. Shown as one
 * `<pre>` capped at its last 5,000 lines — the panel before this story — even
 * the plan was out of reach.
 *
 * **It appends rather than replaces.** Each read passes the previous cursor and
 * receives only what is new, and each page is classified once, as it arrives,
 * by the stateful classifier in `shared` — see `log-model.ts`. So a fold
 * someone opened stays open while a live run keeps writing under it, and
 * nothing already read is read again: O(total) over a run, the IM-83 property.
 *
 * **It is not redacted here.** The lines were redacted in the customer's account
 * before they were written. A console-side filter would be reassurance rather
 * than a control — the data would already have left their account to reach it.
 *
 * **It is not virtualised, still.** A virtualised log breaks find-in-page and
 * select-all, which is most of what a log panel is for. Folding is what keeps
 * the DOM small instead: every line on screen is real DOM text, and the ones
 * that are not on screen — a collapsed body, a hidden tick — are what the
 * in-panel search is for, which the panel says in words.
 *
 * **Terminal escapes are stripped for display and nowhere else.** The download
 * is exactly what the customer's log group holds, escapes and all: that is
 * their record of what was run, and quietly rewriting it is not ours to do.
 */

/**
 * Events per read: CloudWatch's own ceiling, which the API clamps to.
 *
 * The old panel read 500 at a time and — for a finished run — only once, so it
 * showed the first page of a 62,000-line apply and nothing else. Reading the
 * whole thing at 500 would be 125 cross-account calls; at this size it is
 * about seven, each also stopped by CloudWatch at 1MB.
 */
export const LOG_PAGE = 10_000

/** A bound on one catch-up read, far past any real log (40 × 10,000 lines). */
const MAX_PAGES_PER_READ = 40

/** How often a live run is read again, once caught up. */
const POLL_MS = 4000

/**
 * The viewer's preferences, kept per browser.
 *
 * A preference, not a permission, and a convenience rather than state anybody
 * relies on — so storage that throws (a private window, blocked site data) is
 * the same as storage that is empty.
 */
const PREFERENCE_KEY = 'qim.run-log.view'

interface Preference {
  readonly filters: readonly LogFilter[]
  readonly showTicks: boolean
}

const readPreference = (): Preference => {
  try {
    const raw = window.localStorage.getItem(PREFERENCE_KEY)
    if (raw === null) return { filters: [], showTicks: false }
    const parsed = JSON.parse(raw) as Partial<Record<keyof Preference, unknown>>
    const filters = Array.isArray(parsed.filters)
      ? LOG_FILTERS.filter((f) => (parsed.filters as unknown[]).includes(f))
      : []
    return { filters, showTicks: parsed.showTicks === true }
  } catch {
    return { filters: [], showTicks: false }
  }
}

const writePreference = (preference: Preference): void => {
  try {
    window.localStorage.setItem(PREFERENCE_KEY, JSON.stringify(preference))
  } catch {
    // Not remembered, which is all a failure here can cost.
  }
}

const FILTER_LABEL: Readonly<Record<LogFilter, string>> = {
  error: 'Errors',
  warning: 'Warnings',
  change: 'Changes',
  progress: 'Progress',
}

const FILTER_BIT: Readonly<Record<LogFilter, number>> = {
  error: LINE.error,
  warning: LINE.warning,
  change: LINE.change,
  progress: LINE.progress,
}

/*
 * Word and shape first, colour third. An error line says `Error:` itself; the
 * class adds a bar down its left edge and a glyph a reader can scan for, and
 * only then a colour — the same rule as the status chips.
 */
const TONE_CLASS: Readonly<Record<LineTone, string>> = {
  error: 'log-error',
  warning: 'log-warning',
  change: 'log-change',
  tick: 'log-tick',
  body: 'log-body',
  plain: '',
}

/** Saves the untouched log, the way the diagnostic bundle saves its file. */
const save = (filename: string, text: string): void => {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = window.document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** The target line with every occurrence of the query marked. */
const marked = (text: string, query: string): ReactNode => {
  const q = query.trim()
  if (q === '') return text
  const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, 'i'))
  return parts.map((part, i) =>
    i % 2 === 1 ? <mark key={i}>{part}</mark> : <Fragment key={i}>{part}</Fragment>,
  )
}

const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n.toLocaleString('en')} ${n === 1 ? one : many}`

export const RunLog = ({
  baseUrl,
  tenantId,
  runId,
  active,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly runId: string
  /** While true, keep reading. A finished run is read to its end once. */
  readonly active: boolean
}): JSX.Element => {
  /*
   * The log, held by a model that is mutated in place and re-rendered by a
   * version number. Copying 60,000 lines into React state per page is exactly
   * the O(total²) IM-83 removed.
   */
  const model = useRef<LogModel>(createLogModel())
  const [, setVersion] = useState(0)
  const [note, setNote] = useState<string | undefined>(undefined)
  const [catchingUp, setCatchingUp] = useState(true)
  const cursor = useRef<string | undefined>(undefined)

  const [preference] = useState(readPreference)
  const [filters, setFilters] = useState<ReadonlySet<LogFilter>>(() => new Set(preference.filters))
  const [showTicks, setShowTicks] = useState(preference.showTicks)
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set())
  const [target, setTarget] = useState<number | undefined>(undefined)
  const [raw, setRaw] = useState(false)
  const [query, setQuery] = useState('')
  /** Which match or error the target came from, for "3 of 17". */
  const [position, setPosition] = useState<
    { readonly kind: 'match' | 'error'; readonly at: number } | undefined
  >(undefined)
  const pane = useRef<HTMLPreElement>(null)

  useEffect(() => {
    let live = true
    let timer: ReturnType<typeof setTimeout> | undefined

    /*
     * Reads forward until the cursor stops moving, then — for a run still
     * going — waits and does it again.
     *
     * CloudWatch returns the token it was given once there is nothing newer,
     * which is the only reliable "end" it has: an empty page with a *new*
     * token is possible mid-stream, so an empty page alone is not the end.
     */
    const read = async (): Promise<void> => {
      /*
       * Rendered on the first page and then once the read has caught up, not
       * per page: the model takes a page in milliseconds, while re-rendering
       * the view is the expensive part — measured at ~0.5s a time on the real
       * apply in a dev build, so seven renders for seven pages was most of the
       * four seconds opening it took.
       */
      let rendered = model.current.end > 0
      let pending = false
      try {
        for (let n = 0; n < MAX_PAGES_PER_READ; n += 1) {
          const sent = cursor.current
          const page = await fetchRunLog(baseUrl, tenantId, runId, sent, false, LOG_PAGE)
          if (!live) return
          if (page.lines.length > 0) {
            model.current.push(page.lines)
            if (rendered) pending = true
            else {
              rendered = true
              setVersion((v) => v + 1)
            }
          }
          if (page.next !== undefined) cursor.current = page.next
          setNote(page.note)
          if (page.next === undefined || page.next === sent) break
        }
      } catch {
        // A failed read is not a failed run; a live one tries again below.
      }
      if (!live) return
      if (pending) setVersion((v) => v + 1)
      setCatchingUp(false)
      if (active) timer = setTimeout(() => void read(), POLL_MS)
    }

    void read()
    /*
     * The cleanup is registered whether or not this run is still going (IM-87):
     * StrictMode mounts, cleans up and mounts again, and a first read nothing
     * could cancel appended twice — every finished run's log rendered doubled.
     */
    return () => {
      live = false
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [baseUrl, tenantId, runId, active])

  // Bring a jump or a match into view, inside the pane rather than the page.
  useEffect(() => {
    if (target === undefined || raw) return
    const line = pane.current?.querySelector(`[data-line="${String(target)}"]`)
    if (line instanceof HTMLElement && typeof line.scrollIntoView === 'function') {
      line.scrollIntoView({ block: 'center' })
    }
  }, [target, raw])

  const m = model.current

  if (m.end === 0) {
    return <p role="status">{note ?? 'Waiting for the first line…'}</p>
  }

  const matches = query.trim() === '' ? [] : m.search(query)
  const errors = m.errors

  const jumpTo = (line: number, from: { kind: 'match' | 'error'; at: number }): void => {
    const header = m.headerOf(line)
    if (header !== undefined && !expanded.has(header)) {
      setExpanded((open) => new Set(open).add(header))
    }
    setRaw(false)
    setTarget(line)
    setPosition(from)
  }

  const step = (kind: 'match' | 'error', direction: 1 | -1 | 0): void => {
    const list = kind === 'match' ? matches : errors
    if (list.length === 0) return
    const current = position?.kind === kind ? position.at : -1
    const at =
      direction === 0
        ? 0
        : current === -1
          ? direction === 1
            ? 0
            : list.length - 1
          : (current + direction + list.length) % list.length
    const line = list[at]
    if (line !== undefined) jumpTo(line, { kind, at })
  }

  const toggleFilter = (filter: LogFilter): void => {
    const next = new Set(filters)
    if (next.has(filter)) next.delete(filter)
    else next.add(filter)
    setFilters(next)
    writePreference({ filters: [...next], showTicks })
  }

  const toggleTicks = (): void => {
    setShowTicks(!showTicks)
    writePreference({ filters: [...filters], showTicks: !showTicks })
  }

  const toggleFold = (header: number): void => {
    setExpanded((open) => {
      const next = new Set(open)
      if (next.has(header)) next.delete(header)
      else next.add(header)
      return next
    })
  }

  const view = layout(m, { filters, showTicks, expanded, target })
  const failures = m.failures
  const tickCount = m.count(LINE.tick)

  const verdict = m.verdict
  const terraformErrors =
    failures.length === 0 ? null : (
      /*
        Above the log, not inside it. The recorded failure on a run says only
        that the installer exited non-zero, which is true and useless; this is
        the line that answers the question.

        **Every distinct failure, not the last one** (IM-141). The installer
        retries four times, so the repetitions are collapsed and counted.

        Beneath the installer's verdict when there is one (IM-186), and no
        longer the alert then: the verdict is the conclusion, these are its
        symptoms, and two alerts read as two separate problems.
      */
      <div
        className="failure-headline"
        {...(verdict === undefined ? { role: 'alert' } : { 'aria-label': 'Terraform errors' })}
      >
        {verdict === undefined ? null : <p className="muted">Terraform also reported:</p>}
        {failures.map((failure) => (
          <div key={failure.summary}>
            <strong>{failure.summary}</strong>
            {failure.resource === undefined && failure.attempts === 1 ? null : (
              <p className="muted">
                {failure.resource}
                {failure.resource !== undefined && failure.at !== undefined
                  ? ` — ${failure.at}`
                  : ''}
                {failure.attempts === 1
                  ? ''
                  : `${failure.resource === undefined ? '' : ' · '}failed on all ${String(failure.attempts)} attempts`}
              </p>
            )}
          </div>
        ))}
      </div>
    )
  const headline = (
    <>
      {verdict === undefined ? null : <VerdictHeadline verdict={verdict} />}
      {terraformErrors}
    </>
  )

  const renderSegment = (segment: LogSegment): JSX.Element => {
    if (segment.type === 'header') {
      const bodyId = `log-${runId}-body-${String(segment.line)}`
      return (
        <Fragment key={`h${String(segment.line)}`}>
          <span
            className={
              segment.line === target ? 'log-line log-change log-target' : 'log-line log-change'
            }
            data-line={segment.line}
            {...(segment.line === target ? { 'aria-current': 'true' as const } : {})}
          >
            {segment.line === target && position?.kind === 'match'
              ? marked(m.display(segment.line), query)
              : m.display(segment.line)}
          </span>
          {segment.bodyLines === 0 ? null : (
            /*
              A real button, so Enter and Space toggle it and a screen reader
              hears whether it is open. The count is in the label because it
              is the reason to open one or not: "⋯ 19,091 lines" is a CRD
              schema, "⋯ 3 lines" is worth a glance.
            */
            <>
              {' '}
              <button
                type="button"
                className="log-fold"
                aria-expanded={segment.expanded}
                aria-controls={segment.expanded ? bodyId : undefined}
                onClick={() => toggleFold(segment.line)}
              >
                {segment.expanded ? '▾ hide ' : '▸ ⋯ '}
                {plural(segment.bodyLines, 'line')}
              </button>
            </>
          )}
          {'\n'}
        </Fragment>
      )
    }
    const isTarget = segment.from === target
    const text: string[] = []
    for (let i = segment.from; i < segment.to; i += 1) text.push(m.display(i))
    const joined = `${text.join('\n')}\n`
    const tone = TONE_CLASS[segment.tone]
    const bodyOf = segment.tone === 'body' ? m.headerOf(segment.from) : undefined
    return (
      <span
        key={`l${String(segment.from)}`}
        className={`log-line ${tone}${isTarget ? ' log-target' : ''}`.trim()}
        data-line={segment.from}
        {...(bodyOf !== undefined && segment.from === bodyOf + 1
          ? { id: `log-${runId}-body-${String(bodyOf)}` }
          : {})}
        {...(isTarget ? { 'aria-current': 'true' as const } : {})}
      >
        {isTarget && position?.kind === 'match' ? (
          <>
            {marked(m.display(segment.from), query)}
            {'\n'}
          </>
        ) : (
          joined
        )}
      </span>
    )
  }

  return (
    <div className="log-viewer">
      {headline}

      <div className="log-toolbar">
        <div className="log-tools" role="group" aria-label="Show only">
          {LOG_FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              className="log-filter ghost"
              aria-pressed={filters.has(filter)}
              onClick={() => toggleFilter(filter)}
            >
              {FILTER_LABEL[filter]}{' '}
              <span className="log-count">{m.count(FILTER_BIT[filter]).toLocaleString('en')}</span>
            </button>
          ))}
          <label className="log-ticks">
            <input type="checkbox" checked={showTicks} onChange={toggleTicks} /> Show progress ticks
          </label>
        </div>

        <div className="log-tools" role="group" aria-label="Errors">
          <button
            className="ghost"
            type="button"
            disabled={errors.length === 0}
            onClick={() => step('error', 0)}
          >
            First error
          </button>
          <button
            type="button"
            className="ghost"
            disabled={errors.length === 0}
            onClick={() => step('error', -1)}
            aria-label="Previous error"
          >
            ↑
          </button>
          <button
            type="button"
            className="ghost"
            disabled={errors.length === 0}
            onClick={() => step('error', 1)}
            aria-label="Next error"
          >
            ↓
          </button>
          <span className="muted" aria-live="polite">
            {errors.length === 0
              ? 'No errors'
              : position?.kind === 'error'
                ? `Error ${String(position.at + 1)} of ${String(errors.length)}`
                : plural(errors.length, 'error')}
          </span>
        </div>

        <form
          className="log-tools"
          role="search"
          onSubmit={(event) => {
            event.preventDefault()
            step('match', 1)
          }}
        >
          <label>
            Search the whole log{' '}
            <input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                if (position?.kind === 'match') setPosition(undefined)
              }}
            />
          </label>
          <button
            type="button"
            className="ghost"
            disabled={matches.length === 0}
            onClick={() => step('match', -1)}
            aria-label="Previous match"
          >
            ↑
          </button>
          <button
            className="ghost"
            type="submit"
            disabled={matches.length === 0}
            aria-label="Next match"
          >
            ↓
          </button>
          <span className="muted" aria-live="polite">
            {query.trim() === ''
              ? ''
              : matches.length === 0
                ? 'No matches'
                : position?.kind === 'match'
                  ? `${String(position.at + 1)} of ${matches.length.toLocaleString('en')}`
                  : plural(matches.length, 'match', 'matches')}
          </span>
        </form>

        <div className="log-tools">
          <button className="ghost" type="button" aria-pressed={raw} onClick={() => setRaw(!raw)}>
            Show raw
          </button>
          <button
            className="ghost"
            type="button"
            onClick={() => save(`qrvey-run-${runId}.log`, m.rawText())}
          >
            Download log
          </button>
        </div>
      </div>

      {/* Said, because the browser's own find cannot know what it cannot see. */}
      <p className="muted log-hint">
        Your browser&rsquo;s find (&#8984;F) searches only what is shown. Folded bodies and hidden
        lines are searched by the box above, which opens what it finds.
        {view.hiddenTicks > 0 ? ` ${plural(view.hiddenTicks, 'progress tick')} hidden.` : ''}
        {view.hiddenByFilter > 0
          ? ` ${plural(view.hiddenByFilter, 'line')} hidden by the filter.`
          : ''}
        {tickCount > 0 && showTicks ? ` Showing ${plural(tickCount, 'progress tick')}.` : ''}
        {catchingUp ? ' Still reading the log…' : ''}
      </p>

      {/* Said rather than silent: the top of this pane is not the top of the run. */}
      {m.start > 0 ? (
        <p className="muted">
          The first {m.start.toLocaleString('en')} lines are no longer held here. The download holds
          what is; the full log is in the customer&rsquo;s own account.
        </p>
      ) : null}

      {raw ? (
        <pre className="run-log log-raw" tabIndex={0} aria-label={`Raw log for run ${runId}`}>
          {m.displayText()}
        </pre>
      ) : (
        <pre
          className="run-log log-view"
          ref={pane}
          tabIndex={0}
          aria-label={`Log for run ${runId}`}
        >
          {view.segments.map(renderSegment)}
        </pre>
      )}
    </div>
  )
}
